import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';

interface SecurityAuditEntry {
  action: string;
  resource: string;
  ip_address?: string;
  user_agent?: string;
}

export const useSecurityAudit = () => {
  const { user } = useAuth();

  const logSecurityEvent = async (entry: SecurityAuditEntry) => {
    if (!user) return;

    try {
      const { error } = await supabase.rpc('log_security_event_secure', {
        action_type: entry.action,
        resource_name: entry.resource,
        event_details: {
          user_agent: navigator.userAgent,
          ip_address: entry.ip_address || null,
          timestamp: new Date().toISOString()
        }
      });

      if (error) {
        throw error;
      }
    } catch (err) {
      logger.warn('Failed to log security event:', toError(err).message);
    }
  };

  const logPageView = (page: string) => {
    if (page.includes('/admin') || page.includes('/settings')) {
      logSecurityEvent({
        action: 'page_view',
        resource: page,
      });
    }
  };

  const logDataAccess = (table: string, action: 'read' | 'write' | 'delete') => {
    if (['healthcare_providers'].includes(table)) {
      logSecurityEvent({
        action: `data_${action}`,
        resource: table,
      });
    }
  };

  const detectSuspiciousActivity = async () => {
    if (!user) return false;
    
    try {
      const attempts = 0;
      
      if (attempts > 3) {
        logSecurityEvent({
          action: 'suspicious_activity',
          resource: 'authentication',
        });
        return true;
      }
      
      return false;
    } catch (err) {
      logger.error('Erreur détection activité suspecte:', toError(err).message);
      return false;
    }
  };

  return {
    logSecurityEvent,
    logPageView,
    logDataAccess,
    detectSuspiciousActivity,
  };
};

export const useRouteProtection = (requiredRole?: string) => {
  const { user } = useAuth();
  const { logSecurityEvent } = useSecurityAudit();

  useEffect(() => {
    const currentPath = window.location.pathname;
    
    if (requiredRole && user) {
      logSecurityEvent({
        action: 'route_access_attempt',
        resource: currentPath,
      });
    }
  }, [user, requiredRole, logSecurityEvent]);

  return {
    hasAccess: !!user,
  };
};
